import React, { useState } from 'react';
import {
  ShareIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { trackShare } from '../../services/shareService';
import { toast } from 'react-hot-toast';

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  image?: string;
  postId?: string; // Optional: if provided, track shares
}

const ShareButtons: React.FC<ShareButtonsProps> = ({ url, title, description, image, postId }) => {
  const [copied, setCopied] = useState(false);

  // Track share action
  const trackShareAction = async (platform: string) => {
    if (postId) {
      try {
        await trackShare(postId, platform, url);
      } catch (error) {
        console.error('Failed to track share:', error);
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied to clipboard!');
      trackShareAction('link');
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator && typeof (navigator as any).share === 'function') {
      try {
        await (navigator as any).share({
          title,
          text: description,
          url,
        });
        trackShareAction('native');
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Failed to share:', error);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(`${title}${description ? ` - ${description}` : ''}`);
    const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
    trackShareAction('twitter');
  };

  const shareToFacebook = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
    trackShareAction('facebook');
  };

  const shareToLinkedIn = () => {
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
    trackShareAction('linkedin');
  };

  const shareToReddit = () => {
    const shareUrl = `https://reddit.com/submit?title=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
    trackShareAction('reddit');
  };

  const supportsWebShare = typeof navigator !== 'undefined' && 'share' in navigator;

  return (
    <div className="flex items-center space-x-2">
      {supportsWebShare && (
        <button
          onClick={handleNativeShare}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Share"
        >
          <ShareIcon className="w-5 h-5 text-gray-600" />
        </button>
      )}
      
      <button
        onClick={shareToTwitter}
        className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
        title="Share on Twitter"
      >
        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </button>

      <button
        onClick={shareToFacebook}
        className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
        title="Share on Facebook"
      >
        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </button>

      <button
        onClick={shareToLinkedIn}
        className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
        title="Share on LinkedIn"
      >
        <svg className="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </button>

      <button
        onClick={shareToReddit}
        className="p-2 rounded-lg hover:bg-orange-50 transition-colors"
        title="Share on Reddit"
      >
        <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-1.772a1.249 1.249 0 0 1-.861-1.22l-.001-.001A1.249 1.249 0 0 1 17.26 4.744zM9.26 4.744a1.249 1.249 0 0 1-.861 2.001v-.028l-2.597 1.772a1.249 1.249 0 1 1-.434-1.243l2.597-1.772a1.249 1.249 0 0 1 1.295-.73zM5.998 8.245a1.249 1.249 0 0 1 .715.922l.006.006a1.249 1.249 0 0 1-1.243 1.387 1.249 1.249 0 0 1-.714-.922 1.249 1.249 0 0 1 1.236-1.393zm11.01 0a1.249 1.249 0 0 1 1.243 1.393 1.25 1.25 0 0 1-.714.922 1.249 1.249 0 0 1-1.243-1.387l.006-.006a1.25 1.25 0 0 1 .718-.924zM8.76 10.44a1.249 1.249 0 0 1 1.243 1.387 1.249 1.249 0 0 1-.714.922l-.006.006a1.249 1.249 0 0 1-1.243-1.387 1.249 1.249 0 0 1 .72-.928zm6.48 0a1.249 1.249 0 0 1 .714.928 1.249 1.249 0 0 1-1.243 1.387l-.006-.006a1.249 1.249 0 0 1-.714-.922 1.249 1.249 0 0 1 1.249-1.387zM12 13.99a2.99 2.99 0 0 0-2.99 2.99 2.99 2.99 0 0 0 2.99 2.99 2.99 2.99 0 0 0 2.99-2.99 2.99 2.99 0 0 0-2.99-2.99zm0 1.248c.966 0 1.742.776 1.742 1.742a1.742 1.742 0 0 1-1.742 1.742 1.742 1.742 0 0 1-1.742-1.742c0-.966.776-1.742 1.742-1.742z"/>
        </svg>
      </button>

      <button
        onClick={handleCopyLink}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        title="Copy link"
      >
        {copied ? (
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
        ) : (
          <ClipboardDocumentIcon className="w-5 h-5 text-gray-600" />
        )}
      </button>
    </div>
  );
};

export default ShareButtons;


